/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import { useCallback, useRef, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import useGameState from '../../hooks/useGameState';

import { useThemeClassName } from '../../contexts/ThemeContext';
import QuestionsIndicator from '../lists/QuestionsIndicator';
import Question from '../questions/Question';
import BaseGame, { GameApi } from './Base';

import styles from '../../../styles/games/questions.module.scss';

interface QuestionsGameProps {
    game: QuestionsGame;
    participation: Participation;
    canPlay: boolean;
    withQuestionsIndicator?: boolean;
    apiRef?: React.MutableRefObject<GameApi>;
    reload: () => void;
    children?: React.ReactNode;
    className?: string;
    questionClassName?: string;
    questionContainerClassName?: string;
    navigationClassName?: string;
    onLoadingChange?: (loading: boolean) => void;
}

function QuestionsGame({
    game,
    participation,
    canPlay,
    withQuestionsIndicator = false,
    reload,
    apiRef: providedApiRef = null,
    children,
    className,
    questionClassName,
    questionContainerClassName,
    navigationClassName,
    onLoadingChange: providedOnLoadingChange = null,
    ...props
}: QuestionsGameProps) {
    const [loading, setLoading] = useState<boolean>(false);
    const themeClassName = useThemeClassName(styles);
    const { type: gameType, questions = null } = game || {};
    const { answers: userAnswers = [] } = participation || {};
    const { currentQuestionIndex, currentQuestion, currentAnswer, allAnswersValid } = useGameState(
        game,
        participation,
    );
    const { id: questionId, type: questionType } = currentQuestion || {};

    const apiRef = useRef<GameApi>(null);

    const onAnswer = useCallback(
        (newAnswer) => {
            const { answerQuestion } = apiRef.current;
            answerQuestion(questionId, newAnswer);
        },
        [questionId],
    );

    const onLoadingChange = useCallback(
        (newLoading: boolean) => {
            setLoading(newLoading);
            if (providedOnLoadingChange !== null) {
                providedOnLoadingChange(newLoading);
            }
        },
        [providedOnLoadingChange],
    );

    return (
        <BaseGame
            game={game}
            participation={participation}
            apiRef={(ref) => {
                apiRef.current = ref;
                if (providedApiRef !== null) {
                    providedApiRef.current = ref;
                }
            }}
            reload={reload}
            canPlay={canPlay}
            withFeedback
            withTimer={'duration' in game}
            isValid={allAnswersValid}
            className={classNames([
                styles.container,
                styles[camelCase(`${gameType}-game`)],
                styles[camelCase(`${questionType}-question`)],
                className,
                themeClassName,
            ])}
            onLoadingChange={onLoadingChange}
            {...props}
        >
            <div className={styles.game}>
                <TransitionGroup className={styles.questions}>
                    {currentQuestion !== null ? (
                        <CSSTransition
                            key={`question-${questionId}`}
                            classNames={styles}
                            timeout={200}
                        >
                            <div
                                className={classNames([
                                    styles.questionContainer,
                                    questionContainerClassName,
                                ])}
                            >
                                <Question
                                    game={game}
                                    participation={participation}
                                    question={currentQuestion}
                                    answer={currentAnswer}
                                    canPlay={canPlay}
                                    loading={loading}
                                    onAnswer={onAnswer}
                                    className={classNames([styles.question, questionClassName])}
                                />
                            </div>
                        </CSSTransition>
                    ) : null}
                </TransitionGroup>
                {withQuestionsIndicator ? (
                    <QuestionsIndicator
                        currentIndex={currentQuestionIndex}
                        questions={questions}
                        answers={userAnswers}
                        currentIconComponent="sparkle"
                        iconComponent="thumb"
                        className={classNames([styles.navigation, navigationClassName])}
                        withProgress
                    />
                ) : null}
                {children}
            </div>
        </BaseGame>
    );
}

export default QuestionsGame;
